import React, { useState } from 'react'
import ItipsForProduct from '../../../../../components/common/i-tips/product'
import { Button, Input, Popover, Select, Tag, Tooltip, Upload } from 'antd'
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons'
import { renderOrderProduct } from '../../../../order/sales-order/sales-order-management-column-model'
import moment from 'moment'
import { ApiRequest } from '../../../../../services/api/api'
import { getCookie } from 'react-use-cookie'
import { getSelectOptions } from '../../../../../components/common/common-form/common-form-select'
import { urlKey } from '../../../../../services/api/api-urls'

const { Option } = Select

const fieldLabelStyle: React.CSSProperties = {
	marginBottom: '4px',
	fontSize: '12px',
	fontWeight: 500,
	color: '#8c8c8c',
	display: 'flex',
	alignItems: 'center',
	gap: '6px',
	lineHeight: '16px'
}

// 修复 MUI 全局样式导致 antd 按钮文字偏下的问题
const buttonStyle: React.CSSProperties = {
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	lineHeight: 1,
	height: '28px',
	borderRadius: '6px'
}

const DeliveryInfoCell = (props: { rowData: any }) => {
	const { rowData } = props
	const [editing, setEditing] = useState(false)
	const [trackingNo, setTrackingNo] = useState(rowData.trackingNo || '')
	const [courierId, setCourierId] = useState<any>(rowData.courier?.courierId)
	const [saving, setSaving] = useState(false)
	const [courierOptions, setCourierOptions] = useState<any[]>([])
	const [courierOptionsLoaded, setCourierOptionsLoaded] = useState(false)
	// 只有 Delivered 页面可以修改 Courier / TrackingNo
	const isDeliveredPage = typeof window !== 'undefined' && window.location.pathname === '/delivered'

	// 首次打开编辑框时才加载 Courier 选项，避免每行都请求
	const loadCourierOptions = () => {
		if (courierOptionsLoaded) return
		setCourierOptionsLoaded(true)
		getSelectOptions(urlKey.Courier)
			.then((res: any) => setCourierOptions(res || []))
			.catch(() => setCourierOptionsLoaded(false))
	}

	const resetValues = () => {
		setTrackingNo(rowData.trackingNo || '')
		setCourierId(rowData.courier?.courierId)
	}

	const onSave = async () => {
		const newTrackingNo = trackingNo.trim()
		const originCourierId = rowData.courier?.courierId
		const trackingChanged = newTrackingNo !== (rowData.trackingNo || '')
		const courierChanged = courierId !== originCourierId
		if (!trackingChanged && !courierChanged) {
			setEditing(false)
			return
		}
		setSaving(true)
		try {
			await ApiRequest({
				url: 'Dispatching/UpdateDispatchDelivery',
				method: 'put',
				data: {
					dispatchId: rowData.dispatchId,
					// 原来有 Courier 而被清空时传 null；原来就没有时不传
					courierId: courierId ?? (originCourierId ? null : undefined),
					trackingNo: newTrackingNo,
					// 后端 Tracking 页权限校验用
					employeeId: parseInt(getCookie('id'))
				}
			})
			setEditing(false)
			window.dispatchEvent(new CustomEvent('refreshDispatchedTable'))
		} catch (e) {
			// 错误提示由 axios 拦截器统一处理
		} finally {
			setSaving(false)
		}
	}

	const renderTags = (clickable: boolean) => (
		<div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
			{rowData.courierName && <Tag color="blue" style={clickable ? { cursor: 'pointer' } : undefined}>{rowData.courierName}</Tag>}
			<div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
				<Tag color="green" style={clickable ? { cursor: 'pointer' } : undefined}>{rowData.trackingNo || '-'}</Tag>
				{clickable && <EditOutlined style={{ color: '#1890ff' }} />}
			</div>
		</div>
	)

	return (
		<Tooltip
			title={
				<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
				{rowData.courierName && <div>Courier: {rowData.courierName}</div>}
				<div>TrackingNo: {rowData.trackingNo || '-'}</div>
				</div>
			}
			>
			{isDeliveredPage ? (
				<div style={{ cursor: 'pointer' }} title="Click to edit courier / tracking no" onClick={(e) => e.stopPropagation()}>
						<Popover
							open={editing}
							onOpenChange={(open) => {
								if (saving) return
								setEditing(open)
								resetValues()
								if (open) loadCourierOptions()
							}}
							trigger="click"
							title={
								<span style={{ fontSize: '13px' }}>
									<EditOutlined style={{ color: '#1890ff', marginRight: '6px' }} />
									Edit Delivery Info
								</span>
							}
							content={
								<div style={{ width: '19rem' }}>
									<div style={{ height: '1px', background: '#f0f0f0', margin: '0 -4px 12px' }} />
									<div>
										<div style={fieldLabelStyle}>
											<span style={{ width: '3px', height: '12px', borderRadius: '2px', background: '#1890ff', display: 'inline-block' }} />
											Courier
										</div>
										<Select
											style={{ width: '100%', borderRadius: '6px' }}
											value={courierId}
											onChange={(v: any) => setCourierId(v)}
											getPopupContainer={(triggerNode: any) => triggerNode.parentElement}
											allowClear
											showSearch
											optionFilterProp="children"
											placeholder="Select courier"
										>
											{courierOptions.map((row: any) => (
												<Option key={row.courierId} value={row.courierId}>{row.courierName}</Option>
											))}
										</Select>
									</div>
									<div>
										<div style={fieldLabelStyle}>
											<span style={{ width: '3px', height: '12px', borderRadius: '2px', background: '#52c41a', display: 'inline-block' }} />
											Tracking No
										</div>
										<Input
											value={trackingNo}
											onChange={(e) => setTrackingNo(e.target.value)}
											onPressEnter={onSave}
											placeholder="Enter tracking no"
											maxLength={100}
											allowClear
											style={{ borderRadius: '6px' }}
										/>
									</div>
									<div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
										<Button block icon={<CloseOutlined />} style={buttonStyle} onClick={() => { resetValues(); setEditing(false) }}>Cancel</Button>
										<Button block type="primary" icon={<CheckOutlined />} style={buttonStyle} loading={saving} onClick={onSave}>Save</Button>
									</div>
								</div>
							}
						>
							{renderTags(true)}
						</Popover>
				</div>
			) : renderTags(false)}
		</Tooltip>
	)
}

export const DispatchedColumnModel = () => {
	// 产品搜索: 同时匹配 productName 和 productCode (任一命中即通过)
	const myfiler=(
		filterValue:any,
		rowData:any
	  ) => {
		const filter = filterValue.replace(/-/g, '\u2011').trim().slice(0,-1).toUpperCase();
		for (let ele of rowData.order.orderProduct){
		  const prodName = ele.product?.productName?.replace(/-/g, '\u2011')?.trim()?.toUpperCase();
		  const prodCode = ele.product?.productCode?.replace(/-/g, '\u2011')?.trim()?.toUpperCase();
		  if (prodName?.indexOf(filter) >=0 ) return true;
		  if (prodCode?.indexOf(filter) >=0 ) return true;
		}
		if (rowData.courier?.courierName?.toUpperCase()?.indexOf(filter) >=0) return true;
		return false;
	  }  
  return [
	{
		title: 'Customer',
		field: 'customerStr'
	},
	{
		title: 'Required delivered method',
		field: 'deliveryMethodName',
	},	
	{
		title: 'Order Products',
		field: 'orderProducts',
		sorting: false,
		filtering: true,
		editable: 'never',
		render: (rowData:any) => (rowData.order && renderOrderProduct(rowData.order, false, rowData.dispatchingDetails,false,true)) || '',
		// customFilterAndSearch: (
		// 	filterValue:any,
		// 	rowData:any
		//   ) => {  //product?.productName
		// 		console.log(rowData)
		// 		console.log(rowData.order.orderProduct.map((e:any)=>{return e.product?.productName}))
		// 	 return !filterValue || ( rowData.order?.orderProduct?.map((e:any)=>{return e.product?.productName}).toString().toUpperCase().includes(filterValue.toUpperCase()))
		//   },   
		customFilterAndSearch:(        filterValue:any,
			rowData:any) =>myfiler(
			filterValue,
			rowData
		  ) 		
	},	
	{
	title: 'TrackingNo / Courier',
	field: 'trackingNo',
	render: (rowData: any) => <DeliveryInfoCell rowData={rowData} />
	},
	{
		title: 'Details',
		field: 'dispatchingDetails',
		filtering: false,
		sorting: false,
        render: (rowData: any) => (
			<div>
				<Popover
					content={
						rowData.dispatchingDetails?.map((row: any, index: any) => (
							<div key={index}>
								<b>BoxCode:</b>&nbsp;{row.box?.barCode || 'null'}&nbsp;
                      			<b>Product:</b>&nbsp;<ItipsForProduct id={row.productId} label={row.box?.product?.productName} showSupplierData={false}/>&nbsp;
								<b>Quantity:</b>&nbsp;{row.quantity}&nbsp;
							</div>
						))
					}
					title="Details"
				>
					<Button type="primary" ghost>Details</Button>
				</Popover>
				{}
			</div>
		)
	},	
	{
		title: 'Delivered At',
		field: 'deliveredAt',
		defaultSort: 'desc',
		render: (rowData: any) => {
			const deliveredAt = rowData.deliveredAt;
			const createdAt = rowData.createdAt1;
			
			if (deliveredAt) {
    			return moment.utc(deliveredAt).local().format('DD/MM/YY HH:mm');
			} else {
				return '-';
			}
		},
	},
	{
		title: 'Po',
		field: 'custOrderNo'
	},	  
	{
		title: 'PackingSlip',
		field: 'packingSlipNo',
		render: (rowData: any) => (
			<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
				<span>{rowData.packingSlipNo || '-'}</span>
				{rowData.status === 3 && (
					<Button
						size="small"
						type="primary"
						onClick={(e) => {
							e.stopPropagation()
							// 触发托盘配置
							window.dispatchEvent(new CustomEvent('openPalletConfig', { detail: rowData }))
						}}
						style={{
							padding: '2px 8px',
							fontSize: '12px',
							height: '24px'
						}}
					>
						Pallet
					</Button>
				)}
			</div>
		)
	},		

	  {
      title: 'Status',
      field: 'status',
      lookup: {0: 'Cancelled', 1: 'To be delivered', 2: 'Delivered', 3: 'Picking', 4: 'On Board'}
	  },
	  {
	    title: 'Photos',
	    field: 'imgs',
	    render: (rowData: any) => rowData.imgs ?
			(<Popover content = {
				<div style={{width: '66rem', display: 'flex', flexWrap: 'wrap'}}>
					{getImgs(rowData.imgs).map((img: any) => (
						<div key={img.name} style={{width: '20rem', height: '20rem', margin: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
							<Upload className="img-preview"
								listType="picture-card"
								fileList={[img]}
								disabled>
							</Upload>
						</div>
					))}
				</div>
			} title="Photo Details">
				<div style={{width: '60px', height: '60px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
					<img style={{width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100%'}} src={getImgs(rowData.imgs)[0]?getImgs(rowData.imgs)[0].url:null} />
				</div>
			</Popover>) : null
	  },

  ]
}

const getImgs = (imgStr: string) => {
  return imgStr ? JSON.parse(imgStr) : null
}
